import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { apiRequest, teamPhoto } from '../../utils';
import Icon from '../../components/common/Icon';
import './Leaderboard.css';

const formatPoints = (n) => {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString();
};

const teamImage = (team) => {
  const photo = teamPhoto(team.photo_url, team.company_picture);
  const contain = !team.photo_url && !!team.company_picture;
  return { photo, contain };
};

/* ----------------------- podium slot ----------------------- */
/* Same markup/design as the in-app board, rendered as a plain node (no team
   link — this page is unauthenticated and there is no personal team here). */
const Slot = ({ team, rank, tone }) => {
  const { photo, contain } = teamImage(team);
  const crownIcon = rank === 1 ? 'crown' : 'medal';

  return (
    <div className={`lb-slot lb-slot--${tone}`}>
      <span className="lb-slot__crown">
        <Icon name={crownIcon} pack="sr" />
      </span>

      <div className="lb-slot__avatar-wrap">
        <div
          className={`lb-slot__avatar ${contain ? 'lb-slot__avatar--contain' : ''}`}
          style={{ backgroundImage: `url(${photo})` }}
        />
        <span className="lb-slot__rank">{rank}</span>
      </div>

      <p className="lb-slot__name">{team.name}</p>
      {team.company_name && <p className="lb-slot__company">{team.company_name}</p>}

      <div className="lb-slot__points">
        {formatPoints(team.points)}
        <span className="lb-slot__points-label">pts</span>
      </div>
    </div>
  );
};

/* ----------------------- list row ----------------------- */

const Row = ({ team, rank }) => {
  const { photo, contain } = teamImage(team);

  return (
    <div className="lb-row">
      {team.house_color && (
        <span className="lb-row__house" style={{ background: team.house_color }} />
      )}

      <div className="lb-row__rank">{rank}</div>

      <div className="lb-row__avatar-wrap">
        <div
          className={`lb-row__avatar ${contain ? 'lb-row__avatar--contain' : ''}`}
          style={{ backgroundImage: `url(${photo})` }}
        />
      </div>

      <div className="lb-row__body">
        <p className="lb-row__name">{team.name}</p>
        {team.company_name && (
          <p className="lb-row__company">
            {team.house_color && (
              <span className="lb-row__house-flag" style={{ background: team.house_color }} />
            )}
            {team.company_name}
          </p>
        )}
      </div>

      <div className="lb-row__points">
        <span className="lb-row__points-num">{formatPoints(team.points)}</span>
        <span className="lb-row__points-label">pts</span>
      </div>
    </div>
  );
};

/* ----------------------- main ----------------------- */

/**
 * Public, login-free ranking for a single event (shared by link / email).
 * Mirrors the in-app /leaderboard design (podium + standings) but is
 * unauthenticated, scoped to just this event, reveals every battle, and drops
 * the pre-ceremony "first X battles" notice and the personal team pin.
 */
const PublicLeaderboard = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [teams, setTeams] = useState([]);
  const [showAlbum, setShowAlbum] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound

  useEffect(() => {
    if (!eventId) { setStatus('notfound'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-leaderboard/${eventId}`)
      .then((res) => {
        if (cancelled) return;
        setEvent(res.event || null);
        setTeams(Array.isArray(res.teams) ? res.teams : []);
        setShowAlbum(!!res.show_album);
        setStatus('ok');
      })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [eventId]);

  const top3 = teams.slice(0, 3);
  const rest = teams.slice(3);
  const title = event?.name || t('leaderboard');

  return (
    <div className="lb">
      {/* HEADER — event name, no back button (opened fresh from a link) */}
      <div className="lb-header">
        <h1 className="lb-header__title">{title}</h1>
      </div>

      {/* LOADING */}
      {status === 'loading' && (
        <>
          <div className="lb-skel-podium" />
          <div className="lb-skel-row" />
          <div className="lb-skel-row" />
          <div className="lb-skel-row" />
        </>
      )}

      {/* NOT FOUND / EMPTY */}
      {(status === 'notfound' || (status === 'ok' && teams.length === 0)) && (
        <div className="lb-empty">{t('noLeaderboardFound')}</div>
      )}

      {/* PODIUM */}
      {top3.length > 0 && (
        <div className="lb-podium">
          <div className="lb-podium__kicker">
            <Icon name="trophy" pack="sr" /> {t('leaderboard')}
          </div>

          <div className="lb-podium__row">
            {top3[1] ? <Slot team={top3[1]} rank={2} tone="silver" /> : <span />}
            {top3[0] && <Slot team={top3[0]} rank={1} tone="gold" />}
            {top3[2] ? <Slot team={top3[2]} rank={3} tone="bronze" /> : <span />}
          </div>
        </div>
      )}

      {/* REST OF TABLE */}
      {rest.length > 0 && (
        <>
          {top3.length > 0 && <div className="lb-divider">{t('rank')}</div>}
          <div className="lb-list">
            {rest.map((team, i) => (
              <Row key={team.id} team={team} rank={i + 4} />
            ))}
          </div>
        </>
      )}

      {/* PHOTOS — after the event, the album is the next thing people come for */}
      {showAlbum && (
        <Link to={`/public-album/${eventId}`} className="lb-album">
          <Icon name="images" />
          <span>{t('viewPhotoAlbum')}</span>
          <Icon name="angle-right" />
        </Link>
      )}
    </div>
  );
};

export default PublicLeaderboard;
